import React from 'react';
import { DatePicker, Select, Button, Form, Spin } from 'antd-v5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useMerchantStores } from '../../../../../hooks/query/useStores';

// Generate time options (30 min intervals)
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += 30) {
      const time = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      const displayHour = hour % 12 || 12;
      const period = hour < 12 ? 'AM' : 'PM';
      const label = `${displayHour}:${min.toString().padStart(2, '0')} ${period}`;
      options.push({ value: time, label });
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

// Normalize time value to HH:MM format (strip seconds if present)
const normalizeTime = time => {
  if (!time) return null;
  // Handle HH:MM:SS format by taking only HH:MM
  const parts = time.split(':');
  if (parts.length >= 2) {
    return `${parts[0]}:${parts[1]}`;
  }
  return time;
};

/**
 * EventSessionForm component for managing a single event date entry
 */
const EventSessionForm = ({ session, index, onUpdate, onRemove, canRemove, errors = {} }) => {
  // Fetch stores from API - only stores with appointments enabled
  const { data: storesData, isLoading: isLoadingStores } = useMerchantStores({
    filters: { appointmentsEnabled: true },
  });
  const stores = storesData?.results || storesData?.data || [];

  const handleDateChange = date => {
    onUpdate({
      date: date ? date.format('YYYY-MM-DD') : null,
    });
  };

  const handleStartTimeChange = value => {
    onUpdate({ startTime: value });
  };

  const handleEndTimeChange = value => {
    onUpdate({ endTime: value });
  };

  const handleStoresChange = values => {
    onUpdate({ storeIds: values });
  };

  const storeOptions = stores.map(store => ({
    label: store.name,
    value: store.id,
  }));

  // Filter end time options to only show times after start time
  const getEndTimeOptions = () => {
    const normalizedStart = normalizeTime(session.startTime);
    if (!normalizedStart) return TIME_OPTIONS;

    return TIME_OPTIONS.filter(opt => opt.value > normalizedStart);
  };

  return (
    <div className="event-session-form">
      <div className="session-row">
        <div className="session-field">
          <Form.Item
            label="Date"
            style={{ marginBottom: 0 }}
            validateStatus={errors.date ? 'error' : ''}
          >
            <DatePicker
              value={session.date ? dayjs(session.date) : null}
              onChange={handleDateChange}
              format="MMMM D, YYYY"
              placeholder="Select date"
              style={{ width: '100%' }}
              disabledDate={current => current && current < dayjs().startOf('day')}
              status={errors.date ? 'error' : ''}
            />
          </Form.Item>
        </div>

        <div className="session-field">
          <Form.Item
            label="Start Time"
            style={{ marginBottom: 0 }}
            validateStatus={errors.startTime ? 'error' : ''}
          >
            <Select
              value={normalizeTime(session.startTime)}
              onChange={handleStartTimeChange}
              options={TIME_OPTIONS}
              placeholder="Select time"
              style={{ width: '100%' }}
              status={errors.startTime ? 'error' : ''}
            />
          </Form.Item>
        </div>

        <div className="session-field">
          <Form.Item
            label="End Time"
            style={{ marginBottom: 0 }}
            validateStatus={errors.endTime ? 'error' : ''}
          >
            <Select
              value={normalizeTime(session.endTime)}
              onChange={handleEndTimeChange}
              options={getEndTimeOptions()}
              placeholder="Select time"
              style={{ width: '100%' }}
              disabled={!session.startTime}
              status={errors.endTime ? 'error' : ''}
            />
          </Form.Item>
        </div>

        <div className="session-field session-field--stores">
          <Form.Item
            label="Stores"
            style={{ marginBottom: 0 }}
            validateStatus={errors.storeIds ? 'error' : ''}
            required
          >
            <Select
              mode="multiple"
              value={session.storeIds || []}
              onChange={handleStoresChange}
              options={storeOptions}
              placeholder="Select stores"
              loading={isLoadingStores}
              notFoundContent={isLoadingStores ? <Spin size="small" /> : 'No stores found'}
              status={errors.storeIds ? 'error' : ''}
            />
          </Form.Item>
        </div>

        {canRemove && (
          <div className="session-field session-field--action">
            <Button
              type="text"
              danger
              size="small"
              icon={<FontAwesomeIcon icon={['far', 'trash-alt']} />}
              onClick={onRemove}
            />
          </div>
        )}
      </div>
    </div>
  );
};

EventSessionForm.propTypes = {
  session: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    date: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    storeIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  }).isRequired,
  index: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  canRemove: PropTypes.bool,
  errors: PropTypes.shape({
    date: PropTypes.bool,
    startTime: PropTypes.bool,
    endTime: PropTypes.bool,
    storeIds: PropTypes.bool,
  }),
};

EventSessionForm.defaultProps = {
  canRemove: true,
  errors: {},
};

export default EventSessionForm;
